import { resolveId } from '@medplum/core'
import type {
  Binary,
  Bundle,
  CodeableConcept,
  Coding,
  ExtractResource,
  Extension as FHIRExtension,
  Parameters as FhirParameters,
  Identifier,
  Practitioner,
  Reference,
  Resource,
  ResourceType,
  Task,
} from '@medplum/fhirtypes'
import { compact, omit, toArray } from 'lodash-es'
import { hasCodeableConcept } from '#src/codeableConcept.ts'
import {
  ELATION_SANDBOX_ID_SYSTEM,
  EXTERNAL_RESOURCE_JSON_SYSTEM,
  identifierTypePlacer,
  QUEUED_EDITS_ORIGIN_SYSTEM,
} from '#src/codeSystems.ts'
import {
  type AnyExtensionKind,
  Extension,
  type ExtensionDataTypes,
  getExtensionMetadata,
  getTagMetadata,
} from '#src/decorators.ts'
import { setIdentifier } from '#src/identifier.ts'
import { invariant } from '#src/invariant.ts'
import {
  type MonitoredResource,
  monitorResource,
} from '#src/monitorResource.ts'
import { createReference } from '#src/references.ts'
import type { QueryTypes } from '#src/search.ts'
import { addTag, getTag, hasTag, removeTag, setTag } from '#src/tags.ts'
import { isOfType, sha256 } from '#src/utils.ts'

// These following resources don't have extensions
type ExtendableFhirResource = Exclude<
  Resource,
  Binary | Bundle | FhirParameters
>

// Export values are JSON-serialized FHIR data. FHIR types are interfaces, which
// lack the implicit index signature JsonValue needs, so any non-nullish value
// is accepted structurally through its toString member.
interface ExportFieldObject {
  toString(): string
}

export interface ExportFields {
  [field: string]: ExportFieldObject | null | undefined
}

type ExtendedResourceOptions = {
  profile?: string | string[] | undefined
}

/**
 * Abstract base class for all FHIR resources.
 * @template T - The specific FHIR resource type.
 */
export abstract class ExtendedResource<T extends Resource = Resource> {
  static readonly RESOURCE_TYPE: ResourceType

  protected resource: T

  /** The FHIR profile(s) to apply to the final FHIR resource */
  private profile?: string | string[]

  /** Whether the resource has been persisted or came from Medplum */
  private isPersisted: boolean

  /**
   * Initializes the resource with the given type and data.
   * @param resourceType - The type of the resource.
   * @param data - The initial data for the resource.
   */
  constructor(
    data?: Omit<T, 'resourceType'>,
    options?: ExtendedResourceOptions
  ) {
    if (!this.fhirType) {
      throw new Error(
        `\`RESOURCE_TYPE\` must be defined for \`${this.constructor.name}\``
      )
    }

    // Filter out any properties that are decorated with @Extension or @Tag
    const extensionMetadata = getExtensionMetadata(this)
    const tagMetadata = getTagMetadata(this)
    const decoratedProperties = [...extensionMetadata, ...tagMetadata].map(
      (p) => p.propertyKey
    )
    this.resource = {
      resourceType: this.fhirType,
      ...(omit(data, decoratedProperties) as any),
    } as T
    this.profile = options?.profile
    invariant(
      !this.resource.id,
      'Resource already has an ID - use fromFhirResource instead of constructor for existing resources'
    )
    this.isPersisted = false
    this.initializeDecoratedProperties()
  }

  static fromFhirResource<TInstance extends ExtendedResource>(
    this: ExtendedResourceConstructor<TInstance>,
    data: ResourceOf<TInstance>,
    options?: ExtendedResourceOptions
  ): TInstance {
    invariant(
      isOfType(data, this.RESOURCE_TYPE),
      `Resource type mismatch: expected ${this.RESOURCE_TYPE} but got ${data.resourceType ?? 'unknown'}`
    )
    // use Object.create to avoid calling the constructor, which can have
    // different signatures in subclasses
    const resourceClass = (resourceClassOverrides.get(this) ??
      this) as ExtendedResourceConstructor<TInstance>
    const instance = Object.create(resourceClass.prototype) as TInstance
    const resource = { ...data } as ResourceOf<TInstance>
    instance.resource = resource
    instance.profile = options?.profile
    instance.isPersisted = !!instance.resource?.id
    instance.initializeDecoratedProperties()
    // no need to call initializeDecoratedProperties because that's handled in the
    // constructor and metadata is associated with the class, not the resource
    return instance
  }

  static referencesQuery(references: (Reference | undefined)[]): QueryTypes {
    return {
      _id: compact(references.map(resolveId)),
      _sort: '-_lastUpdated',
      _count: '1000',
    }
  }

  /** we call it fhirType to avoid confusion with resourceType and so ExtendedResource don't pass the isResource check */
  get fhirType(): T['resourceType'] {
    return (this.constructor as typeof ExtendedResource<T>).RESOURCE_TYPE
  }

  get id(): string {
    if (!this.isPersisted) {
      throw new Error('Resource is not persisted and does not have an id')
    }
    invariant(
      this.resource.id,
      'Resource must have an ID when accessing the id property'
    )
    return this.resource.id
  }

  get reference(): Reference<T> & { reference: string } {
    return createReference(this.resource)
  }

  get project(): string | undefined {
    return this.resource.meta?.project
  }

  identifierByType(type: CodeableConcept): Identifier | undefined {
    return this.identifiers().find((id) => hasCodeableConcept(id.type, type))
  }

  identifier(system: string): Identifier | undefined {
    return this.identifiers(system).find((id) => id.use !== 'old')
  }

  identifiers(system?: string): Identifier[] {
    if (!('identifier' in this.resource)) {
      return []
    }
    const identifier = this.resource.identifier
    const identifiers = Array.isArray(identifier)
      ? identifier
      : compact([identifier])
    return identifiers.filter((id) => (system ? id?.system === system : true))
  }

  protected addIdentifier(
    system: string,
    value: string,
    rest: Partial<Omit<Identifier, 'system' | 'value' | 'use'>> = {}
  ): Identifier {
    const resource = this.resource as Resource & { identifier?: Identifier[] }
    const newIdentifier = { system, value, ...rest } satisfies Identifier
    resource.identifier ??= []
    const existingIdentifier = this.identifiers(system).find(
      (id) => id.value === value
    )
    if (!existingIdentifier) {
      resource.identifier.push(newIdentifier)
      return newIdentifier
    } else {
      // unmark from old to official
      existingIdentifier.use = 'official'
      Object.assign(existingIdentifier, rest)
      return existingIdentifier
    }
  }

  setIdentifier(
    system: string,
    value: string,
    rest: Partial<Omit<Identifier, 'system' | 'value'>> = {}
  ): void {
    setIdentifier(
      this.resource as Resource & { identifier?: Identifier[] },
      system,
      value,
      rest
    )
  }

  // Removes every identifier of the system, or only the one with `value`.
  protected removeIdentifier(system: string, value?: string): void {
    const resource = this.resource as Resource & { identifier?: Identifier[] }
    const identifiers = (resource.identifier ?? []).filter(
      (i) => i.system !== system || (value !== undefined && i.value !== value)
    )
    if (identifiers.length > 0) {
      resource.identifier = identifiers
    } else {
      // Empty FHIR arrays fail validation. Deleting the key, rather than
      // assigning undefined, leaves nothing behind for a later delete to
      // turn into a second `remove /identifier` op.
      delete resource.identifier
    }
  }

  protected markIdentifiersAsOld(system: string): void {
    const identifiers = this.identifiers(system)
    identifiers.forEach((id) => {
      id.use = 'old'
    })
  }

  get elationId(): number | undefined {
    const identifier = this.identifier(ELATION_SANDBOX_ID_SYSTEM)
    return identifier?.value ? Number(identifier.value) : undefined
  }

  set elationId(value: number | string | undefined) {
    if (this.elationId) {
      this.markIdentifiersAsOld(ELATION_SANDBOX_ID_SYSTEM)
    }
    if (value) {
      this.addIdentifier(ELATION_SANDBOX_ID_SYSTEM, value.toString(), {
        type: identifierTypePlacer,
      })
    }
  }

  scriptOnlyAddOldElationIds(...values: (number | string)[]): void {
    values.forEach((value) => {
      const identifier = this.addIdentifier(
        ELATION_SANDBOX_ID_SYSTEM,
        value.toString()
      )
      identifier.use = 'old'
    })
  }

  static byIdentifier(system: string, value?: string): QueryTypes {
    return {
      identifier: `${system}|${value ?? ''}`,
    }
  }

  static byElationId(elationId?: number | string): QueryTypes {
    return ExtendedResource.byIdentifier(
      ELATION_SANDBOX_ID_SYSTEM,
      elationId?.toString()
    )
  }

  get tags(): Coding[] {
    return this.resource.meta?.tag ?? []
  }

  get lastUpdated(): string {
    if (!this.isPersisted) {
      throw new Error(
        'Resource is not persisted and does not have a last updated date'
      )
    }
    const lastUpdated = this.resource.meta?.lastUpdated
    invariant(
      lastUpdated,
      'Resource must have a lastUpdated date in meta when accessing lastUpdated property'
    )
    return lastUpdated
  }

  get lastUpdatedDate(): Date {
    return new Date(this.lastUpdated)
  }

  // The author when the server recorded one; fixtures and older records may
  // carry none.
  get metaAuthor(): Reference | undefined {
    return this.resource.meta?.author
  }

  get lastUpdatedBy(): Reference {
    if (!this.isPersisted) {
      throw new Error(
        'Resource is not persisted and does not have a last update author'
      )
    }
    const lastUpdatedBy = this.resource.meta?.author
    invariant(
      lastUpdatedBy,
      'Resource must have an author reference in meta when accessing lastUpdatedBy property'
    )
    return lastUpdatedBy
  }

  get versionId(): string {
    invariant(
      this.resource.meta?.versionId,
      'Resource must have a versionId in meta when accessing versionId property'
    )
    return this.resource.meta.versionId
  }

  get resourceAuthor(): Reference | undefined {
    return this.resource.meta?.author
  }

  get lastUpdatedByPractitioner(): Reference<Practitioner> | undefined {
    const lastUpdatedBy = this.resourceAuthor
    return isOfType(lastUpdatedBy, 'Practitioner') ? lastUpdatedBy : undefined
  }

  // The queued-edits review record this version was written by, if it carries
  // the queued-edits origin tag — the proposal Bundle (`Bundle/<id>`), or the
  // QueuedEditsReviewTask (`Task/<id>`) when one orchestrates the review.
  // Generic across every resource type so any activity feed can render an "as
  // part of … review" backlink without bespoke wiring. Note: the tag persists
  // onto later versions too — use `introducedQueuedEditsOriginTask` to detect
  // the version that first applied it (so the backlink shows once, not on every
  // subsequent edit).
  get queuedEditsOriginTask(): Reference<Task | Bundle> | undefined {
    const tag = getTag(this.resource, QUEUED_EDITS_ORIGIN_SYSTEM)
    return tag?.code ? { reference: tag.code, display: tag.display } : undefined
  }

  /**
   * Called after the extension normalization to FHIR, but before the value is returned.
   *
   * This is useful for setting values programmatically.
   */
  formatFhirResource(resource: T): T {
    return resource
  }

  toFhirResource(): T {
    const output = { ...this.resource }
    if (this.profile) {
      output.meta ??= {}
      output.meta.profile = Array.isArray(this.profile)
        ? this.profile
        : [this.profile]
    }
    return this.formatFhirResource(output)
  }

  hasProfile(profile: string): boolean {
    return this.resource.meta?.profile?.includes(profile) ?? false
  }

  /**
   * Protected method that returns the fields to be included in JSON export.
   * Override this method in child classes to specify additional fields.
   * @returns Record of field names and their values to be included in export
   */
  protected getExportFields(): ExportFields {
    return {}
  }

  /**
   * Internal method that collects all export fields through the inheritance chain
   * @returns Combined record of all export fields
   */
  private getAllExportFields(): ExportFields {
    let currentPrototype = Object.getPrototypeOf(this)
    const exportFields: ExportFields[] = []

    while (currentPrototype?.getExportFields) {
      exportFields.unshift(currentPrototype.getExportFields.call(this))
      currentPrototype = Object.getPrototypeOf(currentPrototype)
    }

    exportFields.push(this.getExportFields())

    return Object.assign({}, ...exportFields)
  }

  toJSONExport(): ExportFields | T {
    const exportFields = this.getAllExportFields()
    if (Object.keys(exportFields).length === 0) {
      return this.toFhirResource()
    }

    const baseFields = {
      elationId: this.elationId,
      identifiers:
        'identifier' in this.resource ? this.resource.identifier : [],
      tags: this.resource.meta?.tag ?? [],
    }

    return {
      ...baseFields,
      ...exportFields,
    }
  }

  toJSONString(
    replacer?: Parameters<typeof JSON.stringify>[1],
    space?: Parameters<typeof JSON.stringify>[2]
  ): string {
    return JSON.stringify(this.toFhirResource(), replacer, space)
  }

  sha256(): Promise<string> {
    return sha256(this.toJSONString())
  }

  static isInstance(_value: unknown): _value is Resource | ExtendedResource {
    return false
  }

  @Extension({
    url: EXTERNAL_RESOURCE_JSON_SYSTEM,
    kind: 'valueString',
  })
  externalResourceJSON?: string

  // ==========================
  // Extension Management
  // ==========================
  private get extensions(): FHIRExtension[] | undefined {
    return 'extension' in this.resource ? this.resource.extension : undefined
  }
  /**
   * Retrieves the value of a specific extension by URL.
   * @template TKind - The expected type of the extension value.
   * @param url - The URL of the extension.
   * @returns The value of the extension or undefined if not found.
   */
  protected getExtensionValue<TKind extends AnyExtensionKind>(
    url: string,
    kind: TKind
  ): ExtensionDataTypes[TKind] | undefined {
    return this.getExtension(url)?.[kind]
  }

  /**
   * Retrieves a whole extension by URL, for extensions that nest others.
   * @param url - The URL of the extension.
   * @returns The extension or undefined if not found.
   */
  protected getExtension(url: string): FHIRExtension | undefined {
    return this.extensions?.find((e) => e.url === url)
  }

  /**
   * Retrieves the value of a specific multiple value extension by URL.
   * @template TKind - The expected type of the extension value.
   * @param url - The URL of the extension.
   * @returns The values of the extension or [] if not found.
   */
  private getExtensionValueMultiple<TKind extends AnyExtensionKind>(
    url: string,
    kind: TKind
  ): ExtensionDataTypes[TKind][] | undefined {
    return this.extensions
      ?.filter((ext) => ext.url === url)
      .map((ext) => ext?.[kind])
  }

  private isExtendableFhirResource(
    resource: Resource
  ): resource is ExtendableFhirResource {
    return !(
      isOfType(resource, 'Binary') ||
      isOfType(resource, 'Bundle') ||
      isOfType(resource, 'Parameters')
    )
  }

  /**
   * Sets the value of a specific extension by URL.
   * @template TKind - The kind of the extension value.
   * @param url - The URL of the extension.
   * @param value - The value to set.
   */
  protected setExtensionValue<TKind extends AnyExtensionKind>(
    url: string,
    kind: TKind,
    value: ExtensionDataTypes[TKind]
  ): void {
    invariant(
      this.isExtendableFhirResource(this.resource),
      `Cannot set extension on resource type ${this.fhirType}`
    )
    this.resource.extension ??= []

    const index = this.resource.extension.findIndex((e) => e.url === url)
    if (index !== -1) {
      this.resource.extension[index] = {
        ...this.resource.extension[index],
        [kind]: value,
      }
    } else {
      this.resource.extension.push({ url, [kind]: value })
    }
  }
  /**
   * Sets the value of a specific multiple value extension by URL.
   * @template TKind - The kind of the extension value.
   * @param url - The URL of the extension.
   * @param value - The value to set.
   */
  private setExtensionMultipleValue<TKind extends AnyExtensionKind>(
    url: string,
    kind: TKind,
    value: ExtensionDataTypes[TKind][]
  ): void {
    invariant(
      this.isExtendableFhirResource(this.resource),
      `Cannot set extension on resource type ${this.fhirType}`
    )
    this.resource.extension ??= []

    const extensions = this.resource.extension.filter((e) => e.url !== url)
    this.resource.extension = [
      ...extensions,
      ...value.map((v) => ({ url, [kind]: v })),
    ]
  }

  /**
   * Removes a specific extension by URL.
   * @param url - The URL of the extension to remove.
   */
  removeExtension(url: string): void {
    invariant(
      this.isExtendableFhirResource(this.resource),
      `Cannot set extension on resource type ${this.fhirType}`
    )
    const newExtensions = this.extensions?.filter((e) => e.url !== url)
    if (newExtensions?.length) {
      this.resource.extension = newExtensions
    } else if (this.extensions !== undefined) {
      delete this.resource.extension
    }
  }

  addExtension(extension: FHIRExtension): void {
    invariant(
      this.isExtendableFhirResource(this.resource),
      `Cannot add extension on resource type ${this.fhirType}`
    )
    this.resource.extension ??= []
    this.resource.extension.push(extension)
  }

  // ==========================
  // Tag Management
  // ==========================

  /**
   * Retrieves a tag by its system.
   * @param system - The system URL of the tag.
   * @returns The Coding object representing the tag or undefined if not found.
   */
  protected getTag(system: string): Coding | undefined {
    return getTag(this.resource, system)
  }

  /**
   * Sets a tag in the resource's metadata.
   * @param system - The system URL of the tag.
   * @param code - The code representing the tag.
   * @param display - (Optional) The display text for the tag.
   */
  protected setTag(
    system: string,
    code: string | undefined,
    display?: string
  ): void {
    setTag(this.resource, { system, code, display })
  }

  /**
   * Removes a tag by its system.
   * @param system - The system URL of the tag to remove.
   */
  private removeTagBySystem(system: string): void {
    this.removeTag({ system })
  }

  /**
   * Retrieves all tags associated with a given system.
   * @param system - The system URL of the tags.
   * @returns An array of Coding objects representing the tags or an empty array if none found.
   */
  protected getTagsBySystem(system: string): readonly Coding[] {
    return this.tags.filter((tag) => tag.system === system)
  }

  /**
   * Sets multiple tags for a given system, replacing any existing tags with that system.
   * @param system - The system URL of the tags.
   * @param tags - An array of Coding objects to set.
   */
  protected setTagsBySystem(system: string, tags: Coding[]): void {
    const otherTags = this.tags.filter((tag) => tag.system !== system)
    const newTags = [...otherTags, ...tags.map((t) => ({ system, ...t }))]

    if (newTags.length > 0) {
      this.resource.meta ??= {}
      this.resource.meta.tag = newTags
    } else if (this.resource.meta?.tag !== undefined) {
      delete this.resource.meta.tag
      if (Object.keys(this.resource.meta).length === 0) {
        delete this.resource.meta
      }
    }
  }

  hasTag(tag: Coding): boolean {
    return hasTag(this.resource, tag)
  }

  addTag(tag: Coding): void {
    addTag(this.resource, tag)
  }

  removeTag(tag: Coding): void {
    removeTag(this.resource, tag)
  }

  /**
   * Initializes properties decorated with @Extension and @Tag.
   */
  private initializeDecoratedProperties(): void {
    getExtensionMetadata(this).forEach(
      ({ propertyKey, url, kind, allowMultiple }) => {
        Object.defineProperty(this, propertyKey, {
          get: () =>
            allowMultiple
              ? this.getExtensionValueMultiple<any>(url, kind)
              : this.getExtensionValue<any>(url, kind),
          set: (value: any) => {
            if (allowMultiple) {
              if (value !== undefined && value.length > 0) {
                this.setExtensionMultipleValue<any>(url, kind, value)
              } else {
                this.removeExtension(url)
              }
            } else if (value !== undefined && value !== '') {
              this.setExtensionValue<any>(url, kind, value)
            } else {
              this.removeExtension(url)
            }
          },
          enumerable: true,
          configurable: true,
        })
      }
    )

    getTagMetadata(this).forEach(({ propertyKey, system, allowMultiple }) => {
      Object.defineProperty(this, propertyKey, {
        get: () =>
          allowMultiple ? this.getTagsBySystem(system) : this.getTag(system),
        set: (value: Coding | Coding[] | undefined) => {
          if (allowMultiple) {
            const tags = toArray(value)
            if (tags.length > 0) {
              this.setTagsBySystem(system, tags)
            } else {
              this.removeTagBySystem(system)
            }
          } else if (value !== undefined && !Array.isArray(value)) {
            this.setTag(system, value.code, value.display)
          } else {
            this.removeTagBySystem(system)
          }
        },
        enumerable: true,
        configurable: true,
      })
    })
  }

  mutate(
    mutateFn: (instance: this) => void
  ): MonitoredResource<T['resourceType']>['patches'] {
    const innerResource = this.resource
    const monitored = monitorResource(this.resource)
    // swap in the monitored resource so we detect json patches
    this.resource = monitored.proxy as T
    mutateFn(this)
    this.resource = innerResource
    return monitored.patches
  }
}

// fromFhirResource() builds instances of the registered replacement instead
// of the base class. Demo mode uses it to swap in a subclass that hides PHI.
const resourceClassOverrides = new Map<unknown, unknown>()

export function overrideResourceClass<T extends ExtendedResource>(
  base: ExtendedResourceConstructor<T>,
  replacement: ExtendedResourceConstructor<T> | undefined
): void {
  if (replacement) {
    resourceClassOverrides.set(base, replacement)
  } else {
    resourceClassOverrides.delete(base)
  }
}

export type ExtendedResourceConstructor<T extends ExtendedResource> = {
  new (...args: any[]): T
  readonly RESOURCE_TYPE: T['fhirType']
  fromFhirResource<TInstance extends T>(
    this: ExtendedResourceConstructor<TInstance>,
    data: ResourceOf<TInstance>,
    options?: ExtendedResourceOptions
  ): TInstance
  byElationId: (typeof ExtendedResource)['byElationId']
  referencesQuery: (typeof ExtendedResource)['referencesQuery']
  isInstance: (typeof ExtendedResource)['isInstance']
}

export type AnyResourceTypeOrCustomResource = ResourceType | ExtendedResource

export type ResourceOf<T extends AnyResourceTypeOrCustomResource> =
  T extends ResourceType
    ? ExtractResource<T>
    : T extends ExtendedResource<infer R>
      ? R
      : never

export type ResourceTypeOf<T extends AnyResourceTypeOrCustomResource> =
  T extends ResourceType
    ? T
    : T extends ExtendedResource<infer R>
      ? R['resourceType']
      : never
